import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
} from "components/mui";
import { getAllContactsApi } from "api";
import { EMAIL_ADDRESS_REGEX } from "constants/canmail";

/**
 * RecipientPicker — build an ad-hoc recipient list on the spot.
 *
 * Two ways to add a recipient:
 *   1. Pick an existing contact from the address book (typeahead over all contacts).
 *   2. Type any email address and press Enter (freeSolo) — the person does not need
 *      to exist in the database.
 *
 * The `value` is the canonical recipient array that both the CANmail Email Sender and
 * the CANmeet invite flow send via `SendAdHocCANmail`. Nothing is persisted as a list.
 *
 * @param {Object} props
 * @param {Array<{email: string, first_name?: string, last_name?: string, contact_id?: number}>} props.value
 * @param {(next: Array) => void} props.onChange - receives the deduped, validated recipient array
 * @param {string} [props.label="Recipients"]
 * @param {number} [props.maxRecipients=500]
 * @param {boolean} [props.disabled=false]
 */

const DEFAULT_MAX = 500;
const MAX_SUGGESTIONS = 50;

/** Coerce a raw contact record (field names vary across endpoints) into a recipient. */
function normalizeContact(raw) {
  const email =
    raw.email_work ||
    raw.email_address ||
    raw.email ||
    raw.Email ||
    raw.emailWork ||
    "";
  if (!email) return null;
  // Field name varies by endpoint: getAllContactsApi returns `contactId`, others use
  // `contact_id` / `id` / `contactID`. Coerce to a positive integer or drop it (a
  // missing id just means the recipient is treated as a non-contact on send).
  const rawId =
    raw.contact_id ?? raw.contactId ?? raw.id ?? raw.contactID ?? raw.ContactID;
  const contactIdNum = Number(rawId);
  return {
    email: String(email).trim(),
    first_name: raw.first_name || raw.firstName || raw.FirstName || "",
    last_name: raw.last_name || raw.lastName || raw.LastName || "",
    contact_id:
      Number.isInteger(contactIdNum) && contactIdNum > 0
        ? contactIdNum
        : undefined,
  };
}

/** "Jane Doe <jane@x.com>" when a name is known, otherwise just the email. */
function recipientLabel(r) {
  const name = `${r.first_name || ""} ${r.last_name || ""}`.trim();
  return name ? `${name} <${r.email}>` : r.email;
}

/** Strip a trailing separator a user may type before Enter, and trim. */
function cleanTyped(raw) {
  return String(raw).trim().replace(/[,;]+$/, "").trim();
}

function RecipientPicker({
  value = [],
  onChange,
  label = "Recipients",
  maxRecipients = DEFAULT_MAX,
  disabled = false,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  // Lazily load the address book once for typeahead suggestions. Free-typed emails
  // still work if this fails, so a load error is non-fatal (logged, not surfaced).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getAllContactsApi();
        if (cancelled) return;
        const normalized = (Array.isArray(data) ? data : [])
          .map(normalizeContact)
          .filter((c) => c && EMAIL_ADDRESS_REGEX.test(c.email));
        setOptions(normalized);
      } catch (err) {
        console.error(
          "[RecipientPicker] Failed to load contacts for suggestions:",
          err
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedEmails = useMemo(
    () => new Set(value.map((r) => r.email.toLowerCase())),
    [value]
  );

  // Only suggest contacts not already added, matched against the current input, capped
  // so a large address book never renders thousands of options at once.
  const filterOptions = (opts, state) => {
    const q = state.inputValue.trim().toLowerCase();
    if (!q) return [];
    const matches = opts.filter((o) => {
      if (selectedEmails.has(o.email.toLowerCase())) return false;
      return (
        o.email.toLowerCase().includes(q) ||
        `${o.first_name} ${o.last_name}`.toLowerCase().includes(q)
      );
    });
    return matches.slice(0, MAX_SUGGESTIONS);
  };

  // Convert Autocomplete's mixed array (kept recipient objects, picked contact options,
  // and freeSolo strings) into the canonical, deduped recipient array.
  const handleChange = (event, newValue, reason) => {
    if (reason === "clear") {
      setInputError("");
      onChange([]);
      return;
    }

    const next = [];
    const seen = new Set();
    let rejected = "";

    for (const item of newValue) {
      let recipient = null;
      if (typeof item === "string") {
        const email = cleanTyped(item);
        if (!email) continue;
        if (!EMAIL_ADDRESS_REGEX.test(email)) {
          rejected = `"${email}" is not a valid email address`;
          continue;
        }
        recipient = { email };
      } else if (item && item.email) {
        recipient = {
          email: item.email.trim(),
          ...(item.first_name ? { first_name: item.first_name } : {}),
          ...(item.last_name ? { last_name: item.last_name } : {}),
          ...(item.contact_id ? { contact_id: item.contact_id } : {}),
        };
      }
      if (!recipient) continue;
      const key = recipient.email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      next.push(recipient);
    }

    if (next.length > maxRecipients) {
      setInputError(`You can add at most ${maxRecipients} recipients.`);
      onChange(next.slice(0, maxRecipients));
      return;
    }

    setInputError(rejected);
    onChange(next);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      disableCloseOnSelect
      disabled={disabled}
      options={options}
      value={value}
      inputValue={inputValue}
      loading={loading}
      onInputChange={(e, v) => {
        setInputValue(v);
        if (inputError) setInputError("");
      }}
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : recipientLabel(option)
      }
      isOptionEqualToValue={(option, val) =>
        (option.email || "").toLowerCase() === (val.email || "").toLowerCase()
      }
      onChange={handleChange}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          // Destructure `key` out of the tag props: spreading a key into JSX warns in
          // React 18. The email is unique, so it doubles as a stable key.
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={option.email || key}
              label={recipientLabel(option)}
              size="small"
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${label}${value.length ? ` (${value.length})` : ""}`}
          placeholder={value.length ? "" : "Search contacts or type an email…"}
          error={!!inputError}
          helperText={
            inputError || "Pick a contact, or type an email and press Enter."
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default RecipientPicker;
